import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useWatch } from "react-hook-form";
import { useMsal } from "@azure/msal-react";
import fetchAbbreviations from "../../data-entry-form/services/abbreviationsService";

const PdfFileNameGenerator = ({ control, formOptions, reloadAbbreviations }) => {
  const [generatedFileName, setGeneratedFileName] = useState("");
  const [abbreviations, setAbbreviations] = useState({});
  const [isLoadingAbbreviations, setIsLoadingAbbreviations] = useState(true);
  const [processedLengths, setProcessedLengths] = useState({
    docId: 0,
    title1: 0,
    title: 0,
    reportDate: 0
  });
  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;

  const docId = useWatch({ control, name: "docId" });
  const title1 = useWatch({ control, name: "title1" });
  const reportDate = useWatch({ control, name: "reportDate" });
  const title = useWatch({ control, name: "title" });
  const isSubmitting = useWatch({ control, name: "isSubmitting" });

  useEffect(() => {
    if (!docId && !title1 && !reportDate && !title) {
      setGeneratedFileName("");
      setProcessedLengths({
        docId: 0,
        title1: 0,
        title: 0,
        reportDate: 0
      });
      if (control && typeof control._formValues !== 'undefined') {
        control._formValues.generatedFileName = "";
      }
    }
  }, [docId, title1, reportDate, title, control]);

  useEffect(() => {
    if (!isSubmitting) {
      setGeneratedFileName("");
      setProcessedLengths({
        docId: 0,
        title1: 0,
        title: 0,
        reportDate: 0
      });
      if (control && typeof control._formValues !== 'undefined') {
        control._formValues.generatedFileName = "";
      }
    }
  }, [isSubmitting, control]);

  useEffect(() => {
    const loadAbbreviations = async () => {
      try {
        setIsLoadingAbbreviations(true);
        const abbrevs = await fetchAbbreviations(instance, accounts, tenantId);
        setAbbreviations(abbrevs);
      } catch (error) {
        console.error("Error loading abbreviations:", error);
      } finally {
        setIsLoadingAbbreviations(false);
      }
    };
    loadAbbreviations();
  }, [instance, accounts, tenantId, reloadAbbreviations]);

  const applyAbbreviations = (text, category) => {
    if (!text || !abbreviations) return text;

    let result = text;
    const categoryAbbrevs = abbreviations[category] || [];
    const validAbbrevs = categoryAbbrevs
      .filter((abbrev) => abbrev.from && abbrev.to)
      .sort((a, b) => b.from.length - a.from.length);

    validAbbrevs.forEach(({ from, to }) => {
      const escapedFrom = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      if (result.toLowerCase() === from.toLowerCase()) {
        result = to;
        return;
      }

      const wordBoundaryRegex = new RegExp(`\\b${escapedFrom}\\b`, "gi");
      const wordBoundaryResult = result.replace(wordBoundaryRegex, to);

      if (wordBoundaryResult === result) {
        const simpleRegex = new RegExp(escapedFrom, "gi");
        const simpleResult = result.replace(simpleRegex, to);
        if (simpleResult !== result) {
          result = simpleResult;
        }
      } else {
        result = wordBoundaryResult;
      }
    });

    return result;
  };

  const formatPdfFileName = (values) => {
    if (
      !values.docId ||
      !values.title1 ||
      !values.reportDate ||
      !values.title
    ) {
      return "";
    }

    const date = new Date(values.reportDate);
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, "");

    const docId = String(values.docId).padStart(5, "0").slice(0, 5);

    const abbreviatedTitle1 = applyAbbreviations(values.title1, "title1");
    const abbreviatedTitle = applyAbbreviations(values.title, "title");

    const truncatedTitle1 = abbreviatedTitle1.slice(0, 59);
    const truncatedTitle = abbreviatedTitle.slice(0, 25);

    setProcessedLengths({
      docId: docId.length,
      title1: truncatedTitle1.length,
      title: truncatedTitle.length,
      reportDate: formattedDate.length
    });

    const fileName = `${docId}_${truncatedTitle1}_${truncatedTitle}_${formattedDate}.pdf`;
    return fileName;
  };

  const handleGenerateFileName = () => {
    const values = {
      docId,
      title1,
      reportDate,
      title,
    };
    const fileName = formatPdfFileName(values);
    setGeneratedFileName(fileName);
    if (control && typeof control._formValues !== 'undefined') {
      control._formValues.generatedFileName = fileName;
    }
  };

  const isFormValid = docId && title1 && reportDate && title;

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }} component="div">
          {isLoadingAbbreviations ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <span>Loading abbreviations...</span>
            </Box>
          ) : (
            generatedFileName || "Generated filename will appear here"
          )}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerateFileName}
          disabled={!isFormValid || isLoadingAbbreviations}
        >
          Generate Filename
        </Button>
      </Box>
      {generatedFileName && (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mt: 1, fontSize: "0.8rem", color: "text.secondary" }}>
          <Typography variant="caption" component="div">
            DOCID: {processedLengths.docId}/5
          </Typography>
          <Typography variant="caption" component="div">
            Title: {processedLengths.title1}/59
          </Typography>
          <Typography variant="caption" component="div">
            Author: {processedLengths.title}/25
          </Typography>
          <Typography variant="caption" component="div">
            Report Date: {processedLengths.reportDate}/8
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default PdfFileNameGenerator;
